import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Card,
  Prose,
  buttonStyles,
  fadeUp,
} from "@comai-portal/design-system";
import {
  BlogPost,
  BLOG_UNAVAILABLE_MESSAGE,
  mapApiPost,
  formatDate,
} from "./blogTypes";
import { blogAPI } from "../../services/api";
import { getCachedPost, getCachedPosts, waitForCache } from "../../services/blogCache";

// CO 536 — the post comes from the gateway's public `/api/blog/:slug`, backed
// by the marketing platform's blog lane. The warm cache (metadata only) seeds
// the first paint when present; the body always arrives from the API. There is
// no bundled fallback: an unreachable API shows the one honest line, an
// unknown slug shows not-found, and neither is confused with the other.
//
// CO 540 — a post in a series shows "Part N of M · <series>" and walks to its
// previous/next part (siblings read from `/api/blog?series=&sort=reading_order`,
// the lane's own order). Related reading resolves the post's `related` slugs
// against the warm list; a slug that does not resolve is not shown — never a
// broken link.
type Resolution = "pending" | "found" | "not-found" | "unavailable";

interface RelatedLink {
  slug: string;
  title: string;
}

function initialPost(slug?: string): BlogPost | undefined {
  if (!slug) return undefined;
  const cached = getCachedPost(slug);
  return cached ? mapApiPost(cached) : undefined;
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | undefined>(() =>
    initialPost(slug),
  );
  const [resolution, setResolution] = useState<Resolution>(() =>
    slug ? "pending" : "not-found",
  );
  const [siblings, setSiblings] = useState<BlogPost[]>([]);
  const [related, setRelated] = useState<RelatedLink[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;

    async function loadPost() {
      try {
        const apiPost = await blogAPI.getPost(slug!);
        if (cancelled) return;
        if (apiPost) {
          setPost(mapApiPost(apiPost));
          setResolution("found");
        } else {
          setResolution("not-found");
        }
      } catch {
        if (!cancelled) setResolution("unavailable");
      }
    }
    loadPost();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // CO 540 — series siblings + related reading, once the post is known.
  const seriesKey = post?.series?.key;
  const relatedKey = post?.related.join(",") ?? "";
  useEffect(() => {
    let cancelled = false;
    if (!post) return;

    async function loadRelationships() {
      if (seriesKey) {
        try {
          const { posts } = await blogAPI.getPosts({ series: seriesKey, sort: "reading_order", limit: 100 });
          if (!cancelled) setSiblings((posts ?? []).map(mapApiPost));
        } catch {
          if (!cancelled) setSiblings([]);
        }
      } else {
        setSiblings([]);
      }

      if (post!.related.length > 0) {
        await waitForCache();
        let rows = getCachedPosts();
        if (!rows) {
          try {
            rows = (await blogAPI.getPosts({ limit: 100 })).posts ?? [];
          } catch {
            rows = [];
          }
        }
        if (cancelled) return;
        const bySlug = new Map(rows.map((r) => [r.slug, r.title]));
        setRelated(
          post!.related
            .filter((s) => bySlug.has(s))
            .map((s) => ({ slug: s, title: bySlug.get(s)! })),
        );
      } else {
        setRelated([]);
      }
    }
    loadRelationships();

    return () => {
      cancelled = true;
    };
    // `post` identity changes on every fetch; the keys below are what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.slug, seriesKey, relatedKey]);

  if (!post) {
    if (resolution === "pending") {
      return <div className="bg-paper text-ink dark:bg-ink dark:text-paper min-h-[40vh]" />;
    }
    const unavailable = resolution === "unavailable";
    return (
      <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
        <Section>
          <Container width="prose" className="text-center">
            <h1 className="text-display-md mb-4">
              {unavailable ? "Blog unavailable" : "Post not found"}
            </h1>
            <p
              role="status"
              className="text-lead text-mute dark:text-mute-soft mb-8"
            >
              {unavailable
                ? BLOG_UNAVAILABLE_MESSAGE
                : "We couldn't find a post at that address."}
            </p>
            <Link to="/" className={buttonStyles({ variant: "primary" })}>
              Back home
            </Link>
          </Container>
        </Section>
      </div>
    );
  }

  const idx = siblings.findIndex((s) => s.slug === post.slug);
  const previous = idx > 0 ? siblings[idx - 1] : undefined;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : undefined;
  const total = siblings.length > 0 ? siblings.length : undefined;

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <Link to="/blog" className={buttonStyles({ variant: "ghost" })}>
              ← Back to blog
            </Link>

            <div className="mt-8 flex items-center gap-3">
              <span className="ds-eyebrow">{post.category}</span>
              {post.readingTime != null && (
                <span className="text-sm text-mute dark:text-mute-soft">
                  {post.readingTime} min read
                </span>
              )}
            </div>

            {post.series && (
              <p className="text-sm text-mute dark:text-mute-soft mt-3">
                Part {post.series.part}
                {total ? ` of ${total}` : ""} ·{" "}
                <Link to={`/blog/series/${post.series.key}`} className="underline">
                  {post.series.title}
                </Link>
              </p>
            )}

            <h1 className="text-display-md text-balance mt-3">{post.title}</h1>
            <p className="text-sm text-mute dark:text-mute-soft mt-4">
              {post.author.name}
              {post.author.role ? `, ${post.author.role}` : ""} ·{" "}
              {formatDate(post.publishedAt)}
            </p>

            {post.imageUrl && (
              <img
                src={post.imageUrl}
                alt=""
                className="w-full rounded-2xl mt-8"
              />
            )}

            <Card className="mt-8">
              <Prose>{post.body}</Prose>
            </Card>

            {post.series && (previous || next) && (
              <nav
                aria-label="Series navigation"
                className="mt-8 flex flex-wrap justify-between gap-4 text-sm"
              >
                {previous ? (
                  <Link to={`/blog/${previous.slug}`} className="underline">
                    ← Previous: {previous.title}
                  </Link>
                ) : (
                  <span />
                )}
                {next && (
                  <Link to={`/blog/${next.slug}`} className="underline text-right">
                    Next: {next.title} →
                  </Link>
                )}
              </nav>
            )}

            {related.length > 0 && (
              <section className="mt-10">
                <h2 className="ds-eyebrow mb-3">Related reading</h2>
                <ul aria-label="Related reading" className="list-none p-0 m-0 grid gap-2">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/blog/${r.slug}`} className="underline">
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
